import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteActivatedEvent,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogService } from '@vectronic/util';
import { BehaviorSubject } from 'rxjs';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';

export type Item<T> = {
  name: string;
  value: T;
};

@Component({
  selector: 'vectronic-chips-autocomplete',
  templateUrl: './chips-autocomplete.component.html',
  styleUrls: ['./chips-autocomplete.component.scss'],
  imports: [
    CommonModule,
    MatChipsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsAutocompleteComponent<T> {
  selectedItems = input<Item<T>[]>();
  items = input<Item<T>[]>();
  label = input<string | undefined>();
  isDisabled = input<boolean>();
  tooltip = input<string>();
  enableAdd = input(true);
  enableConfirmationOnRemove = input(true);
  @ViewChild('itemInput', { read: MatAutocompleteTrigger })
  autocompleteTrigger: MatAutocompleteTrigger;

  filteredItems = computed(() => {
    const items = this.inputSignal()
      ? this.items().filter(({ name }) =>
          name.toLowerCase().includes(this.inputSignal().toLowerCase()),
        )
      : this.items()?.slice();
    return items?.filter(
      (item) =>
        !this.selectedItems() ||
        this.selectedItems().every((selectedItem) => selectedItem.value !== item.value),
    );
  });
  control = new FormControl('');
  inputSignal = toSignal(this.control.valueChanges, { initialValue: '' });
  separatorKeysCodes$: BehaviorSubject<number[]> = new BehaviorSubject([ENTER, COMMA]);

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  addItem = output<string>();
  selectItem = output<Item<T>>();
  deleteItem = output<Item<T>>();
  textChange = output<string>();

  private dialogService = inject(DialogService);

  onAdd(event: MatChipInputEvent) {
    if (!this.enableAdd()) {
      return;
    }
    const inputValue = (event.value || '').trim();
    if (!inputValue) return;
    this.addItem.emit(inputValue);
    event.chipInput?.clear();
    this.control.setValue(null);
    if (this.input?.nativeElement.value !== undefined) {
      this.input.nativeElement.value = '';
    }
  }

  addButton() {
    this.onAdd({
      input: this.input?.nativeElement,
      value: this.control.value,
      chipInput: null as any,
    });
  }

  remove(item: Item<T>) {
    if (this.enableConfirmationOnRemove()) {
      this.dialogService.open(ConfirmationDialogComponent, {
        afterConfirm: () => this.deleteItem.emit(item),
      });
    } else {
      this.deleteItem.emit(item);
    }
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (!value) return;
    const item: Item<T> = this.items()?.find((item) => item.value === value || item.name === value);
    this.selectItem.emit(item);
    this.control.setValue(null);
    if (this.input?.nativeElement.value !== undefined) {
      this.input.nativeElement.value = '';
    }
  }

  focusInput(): void {
    setTimeout(() => this.input?.nativeElement.focus());
  }

  optionActivated(event: MatAutocompleteActivatedEvent) {
    const anySelected = Boolean(event.option);
    if (anySelected) this.separatorKeysCodes$.next([COMMA]);
    else this.separatorKeysCodes$.next([ENTER, COMMA]);
  }
}
